import { ZONE_RADIUS_METERS } from '@chukua/common';
import { Button, InputNumber, Segmented, Tag } from 'antd';
import type { ZoneDrawMode, ZoneShapeValue } from '../policies/zone-draw-features';

interface ZoneDrawControlsProps {
  shape: ZoneShapeValue;
  /** Null while the map is still loading (or has no key), when there is nothing to draw on. */
  drawMode: ZoneDrawMode | null;
  onDrawModeChange: (mode: ZoneDrawMode) => void;
  onRadiusChange: (radiusMeters: number | null) => void;
  onClear: () => void;
}

const MODE_HINTS: Record<ZoneDrawMode, string> = {
  select: 'Drag a corner to move it, drag a midpoint to add one, or drag the shape to move it all.',
  polygon: 'Click the map to place each corner; click the first corner again to close the shape.',
  point: 'Click the map to place the centre, then set how far the zone reaches. For a place with no drawn boundary, such as a town or a border.',
};

const POINT_EDIT_HINT = 'Drag the centre to move the zone; the radius sets how far it reaches.';

function shapeLabel({ polygon, centroid }: ZoneShapeValue): { label: string; color: string } {
  if (polygon) return { label: 'Polygon', color: 'success' };
  if (centroid) return { label: 'Point and radius', color: 'processing' };
  return { label: 'Not placed', color: 'warning' };
}

/** How the zone is drawn on the map beside the panel. A new shape replaces the old one once it is finished. */
export function ZoneDrawControls({ shape, drawMode, onDrawModeChange, onRadiusChange, onClear }: ZoneDrawControlsProps) {
  const { label, color } = shapeLabel(shape);
  const hasShape = Boolean(shape.polygon || shape.centroid);
  const isPoint = !shape.polygon && Boolean(shape.centroid);

  return (
    <section aria-label="Shape" className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-fg">Shape</span>
        <Tag color={color}>{label}</Tag>
      </div>
      <Segmented<ZoneDrawMode>
        block
        size="small"
        disabled={drawMode === null}
        value={drawMode ?? undefined}
        onChange={onDrawModeChange}
        options={[
          { value: 'select', label: 'Edit', disabled: !hasShape },
          { value: 'polygon', label: 'Polygon' },
          { value: 'point', label: 'Point' },
        ]}
      />
      {drawMode && <p className="text-xs text-muted">{drawMode === 'select' && isPoint ? POINT_EDIT_HINT : MODE_HINTS[drawMode]}</p>}
      {isPoint && (
        <label className="flex items-center justify-between gap-3 text-sm text-fg">
          Radius
          <InputNumber<number>
            aria-label="Radius"
            className="w-36"
            min={ZONE_RADIUS_METERS.min}
            max={ZONE_RADIUS_METERS.max}
            step={100}
            precision={0}
            suffix="m"
            status={shape.radiusMeters ? undefined : 'error'}
            value={shape.radiusMeters}
            onChange={onRadiusChange}
          />
        </label>
      )}
      <div>
        <Button size="small" disabled={drawMode === null || !hasShape} onClick={onClear}>
          Clear shape
        </Button>
      </div>
    </section>
  );
}
